import { useState } from "react";
import { PrismButton, PrismCheckbox, PrismInputText, PrismPopover } from "@prism-ui/react";
import "./Projects.css";

export interface FilterOption {
  value: string;
  display: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: FilterOption[];
  selected: string[];
  disabled?: boolean;
  /** Show a type-ahead box at the top of the menu (useful for long option lists). */
  searchable?: boolean;
  onChange: (values: string[]) => void;
}

/**
 * A compact filter control: a small pill trigger that opens a popover of
 * checkboxes. Multi-select is additive — checking adds, unchecking removes —
 * and the full list of options stays hidden until the pill is opened.
 */
export function MultiSelectDropdown({
  label,
  options,
  selected,
  disabled,
  searchable,
  onChange,
}: MultiSelectDropdownProps) {
  const count = selected.length;
  const [query, setQuery] = useState("");

  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions =
    searchable && normalizedQuery
      ? options.filter((o) => o.display.toLowerCase().includes(normalizedQuery))
      : options;

  const toggle = (value: string, checked: boolean) => {
    if (checked) {
      if (!selected.includes(value)) onChange([...selected, value]);
    } else {
      onChange(selected.filter((v) => v !== value));
    }
  };

  return (
    <PrismPopover
      as="context"
      placement="bottom-start"
      arrow={false}
      className="projects-filter-dropdown"
    >
      <PrismButton
        slot="reference"
        className={`projects-filter-trigger${count > 0 ? " is-active" : ""}`}
        tone="secondary"
        display="outline"
        size="sm"
        disabled={disabled}
        aria-label={`Filter by ${label}`}
      >
        {/* The badge is ALWAYS rendered (visibility toggled via CSS) rather than
            conditionally mounted. React cannot reliably insertBefore a new child
            into a custom-element host whose light-DOM children it does not fully
            own — doing so throws "insertBefore ... not a child of this node" when
            the count flips 0->1 (e.g. a tag-cloud/timeline cross-filter click). */}
        <span className="projects-filter-trigger-label">{label}</span>
        <span
          className={`projects-filter-badge${count > 0 ? "" : " is-hidden"}`}
          aria-hidden={count === 0}
        >
          {count > 0 ? count : ""}
        </span>
        <span className="projects-filter-caret" aria-hidden="true" />
      </PrismButton>

      <div className="projects-filter-dropdown-body">
        {searchable && (
          <div className="projects-filter-typeahead-row">
            <PrismInputText
              className="projects-filter-typeahead"
              type="search"
              label={`Filter ${label} options`}
              hideLabel
              size="sm"
              placeholder={`Filter ${label}…`}
              value={query}
              onPrismInput={(e: CustomEvent<{ value?: string }>) =>
                setQuery(String(e.detail?.value ?? ""))
              }
            />
          </div>
        )}

        <div className="projects-filter-menu" role="group" aria-label={`Filter by ${label}`}>
          {options.length === 0 ? (
            <p className="projects-filter-empty">No options</p>
          ) : visibleOptions.length === 0 ? (
            <p className="projects-filter-empty">No matches</p>
          ) : (
            visibleOptions.map((o) => (
              <PrismCheckbox
                key={o.value}
                className="projects-filter-check"
                name={`filter-${label}-${o.value}`}
                label={o.display}
                value={o.value}
                checked={selected.includes(o.value)}
                onPrismChange={(e: CustomEvent<{ checked: boolean }>) =>
                  toggle(o.value, e.detail.checked)
                }
              />
            ))
          )}
        </div>
      </div>
    </PrismPopover>
  );
}
